/**
 * TabbedPDFViewer Component
 * =========================
 * A React component for displaying multiple PDFs with a dropdown selector.
 * Used for ambulance claims review to switch between F2F, PCS, and PCR documents.
 * 
 * Fetches PDFs via GET request with authentication and displays using blob URLs.
 */

import React, { useState, useEffect, useRef } from 'react';
import { 
  Card, 
  Button, 
  Space, 
  Tooltip, 
  Spin, 
  Typography, 
  Select,
  Divider,
  message,
  Alert
} from 'antd';
import {
  DownloadOutlined,
  FileTextOutlined,
  ExclamationCircleOutlined,
  EyeOutlined,
  FilePdfOutlined,
  ReloadOutlined
} from '@ant-design/icons';

const { Text } = Typography;
const { Option } = Select;

/**
 * TabbedPDFViewer Component
 * 
 * @param {Object} props
 * @param {Object} props.data - Data containing documents array and default selection
 * @param {Array} props.data.documents - Array of document objects [{id, label, filename, url}]
 * @param {string} props.data.default_selected - ID of default selected document
 * @param {string} props.baseUrl - Base URL for the backend API (e.g., 'http://localhost:8035')
 * @param {string} props.title - Component title
 * @param {string} props.sessionId - Session ID for authentication
 * @param {string} props.uuid - Component UUID
 * @param {Function} props.registerComponent - Function to register for updates
 */
const TabbedPDFViewer = ({ 
  data, 
  baseUrl = '',
  title = "Source Documents",
  sessionId,
  uuid,
  registerComponent
}) => {
  // Extract data from props
  const {
    documents = [],
    default_selected = null
  } = data || {};

  // State
  const [selectedDocId, setSelectedDocId] = useState(default_selected || documents[0]?.id || null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  
  // Refs
  const containerRef = useRef(null);

  // Get currently selected document
  const selectedDoc = documents.find(doc => doc.id === selectedDocId) || documents[0] || null;

  // Register component for updates
  useEffect(() => {
    if (registerComponent && uuid) {
      const unregister = registerComponent(() => {
        console.log('TabbedPDFViewer refresh triggered');
        // Refresh the current PDF
        if (selectedDoc) {
          fetchPdf(selectedDoc);
        }
      });
      return unregister;
    }
  }, [registerComponent, uuid, selectedDoc]);

  // Cleanup blob URL on unmount or when it changes
  useEffect(() => {
    return () => {
      if (pdfBlobUrl) {
        window.URL.revokeObjectURL(pdfBlobUrl);
      }
    };
  }, [pdfBlobUrl]);

  // Fetch PDF when selected document changes
  useEffect(() => {
    if (selectedDoc) {
      fetchPdf(selectedDoc);
    }
  }, [selectedDocId, selectedDoc?.url, baseUrl, sessionId]);

  // Fetch PDF from backend
  const fetchPdf = async (doc) => {
    if (!doc?.url) {
      setError('No document URL available');
      return;
    }

    setLoading(true);
    setError(null);

    // Revoke previous blob URL
    if (pdfBlobUrl) {
      window.URL.revokeObjectURL(pdfBlobUrl);
      setPdfBlobUrl(null);
    }

    try {
      // Build full URL
      const fullUrl = baseUrl ? `${baseUrl}${doc.url}` : doc.url;
      console.log('Fetching PDF from:', fullUrl);

      const response = await fetch(fullUrl, {
        method: 'GET',
        headers: sessionId ? {
          'Authorization': `Bearer ${sessionId}`
        } : {},
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch PDF: ${response.status} ${response.statusText}`);
      }
      
      const blob = await response.blob();
      
      // Verify it's a PDF
      if (!blob.type.includes('pdf') && !blob.type.includes('octet-stream')) {
        console.warn('Response may not be a PDF, content-type:', blob.type);
      }

      const blobUrl = window.URL.createObjectURL(blob);
      setPdfBlobUrl(blobUrl);
      setLoading(false);
      
    } catch (err) {
      console.error('PDF fetch error:', err);
      setError(err.message || 'Failed to load PDF');
      setLoading(false);
    }
  };

  // Handle document selection change
  const handleDocumentChange = (docId) => {
    setSelectedDocId(docId);
  };

  // Retry loading
  const handleRetry = () => {
    if (selectedDoc) {
      fetchPdf(selectedDoc);
    }
  };

  // Download handler
  const handleDownload = async () => {
    if (!selectedDoc?.url) {
      message.error('No document URL available');
      return;
    }

    try {
      const fullUrl = baseUrl ? `${baseUrl}${selectedDoc.url}` : selectedDoc.url;
      
      const response = await fetch(fullUrl, {
        method: 'GET',
        headers: sessionId ? {
          'Authorization': `Bearer ${sessionId}`
        } : {},
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error('Download failed');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = selectedDoc.filename || `${selectedDoc.label}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      message.success('Download started');
    } catch (err) {
      console.error('Download error:', err);
      message.error('Failed to download PDF');
    }
  };

  // Open PDF in new tab
  const handleOpenInNewTab = () => {
    if (pdfBlobUrl) {
      window.open(pdfBlobUrl, '_blank');
    } else if (selectedDoc?.url) {
      const fullUrl = baseUrl ? `${baseUrl}${selectedDoc.url}` : selectedDoc.url;
      window.open(fullUrl, '_blank');
    }
  };

  // If no documents, show placeholder
  if (!data || !documents || documents.length === 0) {
    return (
      <Card 
        title={
          <Space>
            <FilePdfOutlined style={{ color: '#6200EE' }} />
            <span>{title}</span>
          </Space>
        }
      >
        <Alert
          message="No Documents Available"
          description="Upload documents to view them here."
          type="info"
          showIcon
        />
      </Card>
    );
  }

  return (
    <Card 
      ref={containerRef}
      title={
        <Space>
          <FilePdfOutlined style={{ color: '#6200EE' }} />
          <span>{title}</span>
        </Space>
      }
      extra={
        <Space>
          <Button 
            icon={<DownloadOutlined />}
            onClick={handleDownload}
            disabled={!selectedDoc}
          >
            Download
          </Button>
          <Button 
            icon={<EyeOutlined />}
            onClick={handleOpenInNewTab}
            disabled={!selectedDoc && !pdfBlobUrl}
          >
            New Tab
          </Button>
        </Space>
      }
      bodyStyle={{ padding: '16px' }}
    >
      {/* Document Selector Dropdown */}
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: '12px',
        marginBottom: '16px',
        padding: '12px 16px',
        backgroundColor: '#fafafa',
        borderRadius: '6px'
      }}>
        <Text strong>Select Document:</Text>
        <Select
          value={selectedDocId}
          onChange={handleDocumentChange}
          style={{ minWidth: 250 }}
          size="middle"
        >
          {documents.map(doc => (
            <Option key={doc.id} value={doc.id}>
              <Space>
                <FileTextOutlined />
                {doc.label}
              </Space>
            </Option>
          ))}
        </Select>
        {selectedDoc && (
          <Text type="secondary" style={{ marginLeft: 'auto' }}>
            {selectedDoc.filename}
          </Text>
        )}
      </div>

      <Divider style={{ margin: '0 0 16px 0' }} />

      {/* PDF Viewer */}
      <div style={{ position: 'relative', minHeight: '400px' }}>
        {/* Loading Spinner */}
        {loading && (
          <div style={{ 
            position: 'absolute', 
            top: '50%', 
            left: '50%', 
            transform: 'translate(-50%, -50%)',
            zIndex: 10,
            textAlign: 'center'
          }}>
            <Spin size="large" />
            <div style={{ marginTop: '12px', color: '#666' }}>Loading PDF...</div>
          </div>
        )}
        
        {error ? (
          // Error State
          <div style={{ 
            padding: '60px 20px',
            textAlign: 'center',
            backgroundColor: '#f5f5f5',
            borderRadius: '8px'
          }}>
            <ExclamationCircleOutlined style={{ fontSize: '48px', color: '#ff4d4f', marginBottom: '16px' }} />
            <div style={{ marginBottom: '16px' }}>
              <Text type="danger">{error}</Text>
            </div>
            <Space>
              <Button 
                icon={<ReloadOutlined />}
                onClick={handleRetry}
              >
                Retry
              </Button>
              <Button 
                type="primary" 
                icon={<DownloadOutlined />}
                onClick={handleDownload}
                style={{ backgroundColor: '#6200EE' }}
              >
                Download PDF
              </Button>
              <Button 
                icon={<EyeOutlined />}
                onClick={handleOpenInNewTab}
              >
                Open in New Tab
              </Button>
            </Space>
          </div>
        ) : pdfBlobUrl ? (
          // PDF Display
          <div style={{ 
            border: '1px solid #e8e8e8',
            borderRadius: '8px',
            overflow: 'hidden',
            backgroundColor: '#525659'
          }}>
            {/* Toolbar */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '8px 16px',
              backgroundColor: '#f5f5f5',
              borderBottom: '1px solid #e8e8e8'
            }}>
              <Space>
                <FileTextOutlined style={{ color: '#6200EE' }} />
                <Text strong>{selectedDoc?.label}</Text>
              </Space>
              <Text type="secondary">{selectedDoc?.filename}</Text>
            </div>
            
            {/* PDF iframe using blob URL */}
            <iframe
              key={pdfBlobUrl} // Force re-render when blob URL changes
              src={pdfBlobUrl}
              style={{
                width: '100%',
                height: '600px',
                border: 'none'
              }}
              title={`PDF Document - ${selectedDoc?.label}`}
            />
          </div>
        ) : !loading && (
          // No PDF loaded yet
          <div style={{ 
            padding: '60px 20px',
            textAlign: 'center',
            backgroundColor: '#f5f5f5',
            borderRadius: '8px'
          }}>
            <FilePdfOutlined style={{ fontSize: '48px', color: '#999', marginBottom: '16px' }} />
            <div>
              <Text type="secondary">Select a document to view</Text>
            </div>
          </div>
        )}
      </div>

      {/* Document Info Footer */}
      <div style={{ 
        marginTop: '12px', 
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        color: '#999',
        fontSize: '12px'
      }}>
        <span>
          Viewing: {selectedDoc?.label}
        </span>
        <span>
          {documents.length} document{documents.length !== 1 ? 's' : ''} available
        </span>
      </div>
    </Card>
  );
};

export default TabbedPDFViewer;